/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormHelperText, Grid, TextField } from '@mui/material';
import { useStyles } from '../../../shared/PolicyDialog/styles';
import { ISelect } from '../../../models/Roles/RolesContracts';
import { LuiSelect } from '@jda/lui-common-component-library-mui5';
import {
  DROPDOWN_MAX_HEIGHT,
  POLICY_NAME_LABEL,
  POLICY_DESC_LABEL,
  POLICY_MODULE_LABEL,
  POLICY_ENTITY_LABEL,
  POLICY_HIERARCHY_LABEL,
  POLICY_ACTIONS_LABEL,
  POLICY_MODULE,
  POLICY_ENTITY,
  POLICY_HIERARCHY,
  POLICY_MODULE_HELPER_TEXT,
  POLICY_ENTITY_HELPER_TEXT,
  POLICY_HIERARCHY_HELPER_TEXT,
  POLICY_ACTION_HELPER_TEXT,
  DISPLAY_NAME,
  SELECT_ALL
} from '../../../shared/PolicyDialog/constants';
import SingleSelectTree from '../../../shared/SingleSelectTree';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import {
  selectDefaultValues,
  selectFilteredValues,
  selectFormValidationValues,
  selectHierarchyValues,
  selectPolicyDataFormValues
} from '../../../store/role/PolicyData/dataSelector';
import {
  resetHierarchyValues,
  setHierarchySelectedHierarchy,
  setHierarchySelectedKeys,
  setPolicyFormData,
  setPolicyModuleDefaultValue,
  updateDataFormError,
  setEditPolicyData,
} from '../../../store/role/PolicyData/dataActionCreator';
import { constructEntities, createHierarchy } from '../../../store/role/PolicyData/data';
import { selectError } from '../../../store/error/errorSelector';
import { compareByProperty } from '../../../utility/commonUtility';
import LuiMultiSelectCustom from '../../../shared/LuiMultiSelectCustom';

const Data = (props: any) => {
  const { edit, rowData, setIsSaveDisabled } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const error = useSelector(selectError);
  const policyData = useSelector(selectPolicyDataFormValues);
  const { entityItems: filteredEntityItems } = useSelector(selectFilteredValues);
  const { moduleItems, entityItems, actionItems } = useSelector(selectDefaultValues);
  const { hierarchyItems, expandedItems, selectedHierarchy, selectedKeys } = useSelector(selectHierarchyValues);
  const { defaultValues: errorFormDefaultValues, errorFormValues: formErrorState } = useSelector(selectFormValidationValues);

  const handleItemClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, item: any) => {
    event.stopPropagation();
    if (!item.checkStatus || item.allowExpandItem) {
      createHierarchy(dispatch, null, item, policyData.entity.key, hierarchyItems, expandedItems);
    }
  };
  useEffect(() => {
    constructEntities(dispatch, rowData);
    !edit && dispatch(setPolicyModuleDefaultValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (edit) {
      dispatch(setEditPolicyData(setRowData(rowData)));
      if (policyData?.entity) {
        createHierarchy(dispatch, policyData.entity, null, policyData.entity.key, hierarchyItems, expandedItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, policyData.entity]);

  const handleSaveButtonDisable = useCallback(() => {
    if (
      policyData.name !== '' &&
      policyData.description !== '' &&
      policyData.module &&
      policyData.module.length !== 0 &&
      (policyData.entity && policyData.entity.key !== 'select') &&
      policyData.hierarchy &&
      policyData.hierarchy.length &&
      policyData.action.length !== 0
    ) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [policyData, setIsSaveDisabled]);

  useEffect(() => {
    handleSaveButtonDisable();
  }, [handleSaveButtonDisable]);

  const setRowData = (rowData: any) => {
    const obj = JSON.parse(rowData.expression);
    return {
      name: rowData.displayName,
      description: rowData.description,
      action: obj.actions.map((action: any, indx: any) => ({ key: `${indx + 1}`, displayName: action })),
      module: obj.modules.map((module: any) => ({ "key": module.id, "displayName": module.name })),
      hierarchy: obj.policy_rules.values || []
    };
  };
  const onSelectionChange = (name: string, event: { target: { name: string; value: ISelect } }) => {
    event.target.name = name;
    if (name === POLICY_ENTITY) {
      dispatch(resetHierarchyValues());
      dispatch(setPolicyFormData(POLICY_HIERARCHY, []));
      createHierarchy(dispatch, event.target.value, null, policyData.entity.key, hierarchyItems, expandedItems);
    }
    dispatch(setPolicyFormData(event.target.name, event.target.value));
    dispatch(updateDataFormError({ ...errorFormDefaultValues }));
  };

  const handleMultiSelectChange = (name: string, event: ISelect[]) => {
    if (name === POLICY_MODULE) {
      constructEntities(dispatch, rowData);
    }
    dispatch(setPolicyFormData(name, event));
    dispatch(updateDataFormError({ ...errorFormDefaultValues }));
  };

  const handleHierarchySelection = (selectedItem: any) => {
    //Set variables
    let rules = policyData.hierarchy;
    if (rules && rules.length > 0) {
      const policyLevel = rules.find((valueItem: { [x: string]: any }) => valueItem[selectedItem.level]);
      if (policyLevel) {
        rules.map((valueItem: { [x: string]: string[][] }) => {
          if (valueItem[selectedItem.level]) {
            if (valueItem[selectedItem.level].indexOf(selectedItem.key) === -1 && selectedItem.selected) {
              selectedKeys.push(selectedItem.key);
              selectedHierarchy.push(selectedItem);
              return valueItem[selectedItem.level].push(selectedItem.key);
            } else if (valueItem[selectedItem.level].indexOf(selectedItem.key) !== -1 && !selectedItem.selected) {
              selectedKeys.splice(selectedKeys.indexOf(selectedItem.key), 1);
              return valueItem[selectedItem.level].splice(valueItem[selectedItem.level].indexOf(selectedItem.key), 1);
            }
          }
          return valueItem;
        });
      } else if (selectedItem.selected) {
        selectedKeys.push(selectedItem.key);
        selectedHierarchy.push(selectedItem);
        rules.push({ [selectedItem.level]: [selectedItem.key] });
      }
    } else if (selectedItem.selected) {
      selectedKeys.push(selectedItem.key);
      selectedHierarchy.push(selectedItem);
      rules.push({ [selectedItem.level]: [selectedItem.key] });
    }
    if (!selectedItem.selected) {
      rules = rules.filter((eachRule: { [x: string]: string[][] }) => {
        let isExists = eachRule[selectedItem.level] ? eachRule[selectedItem.level].length : 1;
        return isExists !== 0;
      });
    }
    //Refresh the state
    dispatch(setHierarchySelectedKeys(selectedKeys));
    dispatch(setHierarchySelectedHierarchy(selectedHierarchy));
    //Update Form Fields
    dispatch(setPolicyFormData(POLICY_HIERARCHY, rules));
    dispatch(updateDataFormError({ ...errorFormDefaultValues }));
  };

  const handleTextFieldOnChange = (e: { target: { name: string; value: any } }) => {
    dispatch(updateDataFormError({ ...errorFormDefaultValues }));
    dispatch(setPolicyFormData(e.target.name, e.target.value));
  };
  const getEntityItems = () => {
    return filteredEntityItems
      ?.map((entity: any) => {
        entity.displayName = entity.displayName ? entity.displayName : entity.key;
        return entity;
      })
      .sort(compareByProperty(DISPLAY_NAME));
  };

  return (
    <Grid container>
      <Grid item xs={6} className={classes.formStyle}>
        <TextField
          id="policyName"
          name="name"
          label={POLICY_NAME_LABEL}
          className={classes.textFieldSpacing}
          fullWidth
          value={policyData?.name}
          variant="standard"
          onChange={handleTextFieldOnChange}
          error={formErrorState.error && formErrorState.errorPolicyNameHelperText !== ''}
          helperText={formErrorState.errorPolicyNameHelperText}
          disabled={edit}
        />
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <TextField
          id="policyDescription"
          name="description"
          label={POLICY_DESC_LABEL}
          className={classes.textFieldSpacing}
          value={policyData?.description}
          variant="standard"
          onChange={handleTextFieldOnChange}
          fullWidth
          error={formErrorState.error && formErrorState.errorPolicyDescriptionHelperText !== ''}
          helperText={formErrorState.errorPolicyDescriptionHelperText}
        />
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <LuiMultiSelectCustom
          id="module-multi-select"
          itemData={moduleItems}
          itemKey="key"
          selectAll
          selectAllDisplayText={SELECT_ALL}
          itemDisplayName="displayName"
          inputLabel={POLICY_MODULE_LABEL}
          selectedItems={policyData?.module}
          maxHeight={DROPDOWN_MAX_HEIGHT}
          onSelectionChange={(e: any) => handleMultiSelectChange('module', e)}
        />
        <FormHelperText>{POLICY_MODULE_HELPER_TEXT}</FormHelperText>
        <FormHelperText
          id="policyModule-helper-text"
          error={formErrorState.error && formErrorState.errorPolicyModuleHelperText !== ''}
        >
          {formErrorState.errorPolicyModuleHelperText}
        </FormHelperText>
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <LuiSelect
          id="entity-dropdown"
          onSelectionChange={(e: any) => onSelectionChange('entity', e)}
          itemData={getEntityItems()}
          inputLabel={POLICY_ENTITY_LABEL}
          defaultValue={entityItems}
          maxHeight={DROPDOWN_MAX_HEIGHT}
          disabled={false}
          selectedItem={policyData?.entity}
        />
        <FormHelperText>{POLICY_ENTITY_HELPER_TEXT}</FormHelperText>
        <FormHelperText
          id="policyEntity-helper-text"
          error={formErrorState.error && formErrorState.errorPolicyEntityHelperText !== ''}
        >
          {formErrorState.errorPolicyEntityHelperText}
        </FormHelperText>
      </Grid>
      <Grid item xs={12} className={classes.formStyle}>
        {policyData?.entity?.displayName !== 'Select' && hierarchyItems.length > 0 && (
          <SingleSelectTree
            handleItemClick={handleItemClick}
            expandedItems={expandedItems}
            textField={{
              id: 'policyHierarchy',
              name: 'policyHierarchy',
              label: POLICY_HIERARCHY_LABEL,
              value: policyData.hierarchy.flatMap((itemValue: any) =>
                Object.keys(itemValue).flatMap(itemKeys => itemValue[itemKeys])
              )
            }}
            tree={{
              data: hierarchyItems,
              selectedItems: [
                ...selectedKeys,
                ...policyData.hierarchy.flatMap((itemValue: any) =>
                  Object.keys(itemValue).flatMap(itemKeys => itemValue[itemKeys])
                )
              ],
              onSelectedItem: handleHierarchySelection
            }}
            errorState={{
              error: formErrorState.error && formErrorState.errorPolicyHierarchyHelperText !== '',
              helperText: formErrorState.errorPolicyHierarchyHelperText
            }}
            helperText={POLICY_HIERARCHY_HELPER_TEXT}
          />
        )}
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <LuiMultiSelectCustom
          id="actions-multi-select"
          itemData={actionItems?.sort(compareByProperty(DISPLAY_NAME))}
          itemKey="key"
          itemDisplayName="displayName"
          inputLabel={POLICY_ACTIONS_LABEL}
          selectedItems={policyData?.action}
          selectAll
          selectAllDisplayText={SELECT_ALL}
          maxHeight={DROPDOWN_MAX_HEIGHT}
          onSelectionChange={(e: any) => handleMultiSelectChange('action', e)}
        />
        <FormHelperText>{POLICY_ACTION_HELPER_TEXT}</FormHelperText>
        <FormHelperText
          id="policyAction-helper-text"
          error={formErrorState.error && formErrorState.errorPolicyActionHelperText !== ''}
        >
          {formErrorState.errorPolicyActionHelperText}
        </FormHelperText>
      </Grid>
      <Grid item xs={12} className={classes.formErrorStyle}>
        {error && <ErrorMessage {...error} />}
      </Grid>
    </Grid>
  );
};

export default Data;
